import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import type { ItemListDTO } from '@erp/rpc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/context/ToastContext';
import { useStageFromItemList } from '@/api/rpc/hooks/inventory-management/useMetadataChangeSets';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: ItemListDTO;
}

/**
 * Stage an item list's products as a new change set for review. The list's
 * keys are resolved server-side (`stageFromItemList`) — nothing large leaves
 * the client — and success navigates straight to the new review.
 */
export function CreateReviewFromListDialog(props: Props) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mount the form only while open so its fields reset on every open. */}
        {props.open && <CreateReviewFromListForm {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function CreateReviewFromListForm({ onOpenChange, list }: Props) {
  const gt = useGT();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const stageFromList = useStageFromItemList();
  // Seeded from the list so the common case is one click.
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState('');

  const canSubmit = name.trim().length > 0 && !stageFromList.isPending;

  const submit = () => {
    stageFromList.mutate(
      {
        itemListId: list.id,
        name: name.trim(),
        description: description.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          showToast({
            title: gt('Change set staged', {
              context: 'toast title after staging products into a change set',
            }),
            message: gt('{count} products staged for review.', {
              context: 'toast message after staging products',
              count: String(res.stagedCount),
            }),
            color: 'success',
            icon: 'check',
          });
          onOpenChange(false);
          void navigate({
            to: '/item-master/reviews/$changeSetId',
            params: { changeSetId: res.changeSetId },
          });
        },
        onError: (error) =>
          showToast({
            title: gt('Error', {
              context: 'stage change set error toast title',
            }),
            message:
              error.message ||
              gt('Failed to stage the change set.', {
                context: 'fallback error when staging a change set fails',
              }),
            color: 'error',
            icon: 'x',
          }),
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="create review from item list dialog title">
            Create review from list
          </T>
        </DialogTitle>
        <DialogDescription>
          {gt(
            'The {count} products of "{name}" will be staged as a new change set for review.',
            {
              context:
                'create review from item list dialog subtitle showing the product count',
              count: list.itemCount.toLocaleString(),
              name: list.name,
            },
          )}
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="review-from-list-name">
            <T context="label for the change set name field">Name</T>
          </Label>
          <Input
            id="review-from-list-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={gt('e.g. Q3 price update', {
              context: 'placeholder for the change set name field',
            })}
            disabled={stageFromList.isPending}
            maxLength={255}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="review-from-list-description">
            <T context="label for the change set description field">
              Description
            </T>
          </Label>
          <Textarea
            id="review-from-list-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={gt('Optional notes about this change set', {
              context: 'placeholder for the change set description field',
            })}
            disabled={stageFromList.isPending}
            rows={3}
            maxLength={2000}
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={stageFromList.isPending}
        >
          <T context="cancel button in the create review from list dialog">
            Cancel
          </T>
        </Button>
        <Button onClick={() => canSubmit && submit()} disabled={!canSubmit}>
          {stageFromList.isPending ? (
            <T context="submit button label while staging is in progress">
              Staging…
            </T>
          ) : (
            <T context="submit button label in the create review from list dialog">
              Create review
            </T>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
